import type { MediaAsset, Playlist, PlaylistItem, Screen } from '@ghostscreen/contracts';
import { i18n } from '../../i18n';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';
import { formatPlaylistLoopDuration } from '../playlists/buildPlaylistListModel';

export type HomePlaylistSort = 'edited' | 'name' | 'screens';

/** Max thumbnails shown on a Home playlist card. */
export const HOME_CARD_PREVIEW_LIMIT = 3;

export type HomePlaylistCard = {
  id: string;
  name: string;
  updatedAt: string;
  itemCount: number;
  itemsLabel: string;
  durationLabel: string | null;
  /** First few items' media, in play order — drives the card thumbnails. */
  previews: MediaAsset[];
  screenCount: number;
  screensLabel: string;
  playing: boolean;
  statusLabel: string;
};

/**
 * Library playlists for Home — excludes screen-owned direct shows.
 * `itemsByPlaylistId` is the draft content; missing entries count as empty.
 */
export function buildHomePlaylistCards(input: {
  playlists: Playlist[];
  itemsByPlaylistId: ReadonlyMap<string, PlaylistItem[]>;
  mediaById: ReadonlyMap<string, MediaAsset>;
  screens: Screen[];
  assignments: ReadonlyMap<string, string | null>;
}): HomePlaylistCard[] {
  const { playlists, itemsByPlaylistId, mediaById, screens, assignments } = input;

  return playlists
    .filter((playlist) => playlist.kind !== 'direct')
    .map((playlist) => {
      const items = itemsByPlaylistId.get(playlist.id) ?? [];
      const assignedScreens = screens.filter(
        (screen) => assignments.get(screen.id) === playlist.id,
      );
      const playing = assignedScreens.some(
        (screen) => getCardConnectionStatus(screen) === 'online',
      );
      const screenCount = assignedScreens.length;
      const itemCount = items.length;
      const totalSeconds = items.reduce((sum, item) => sum + item.durationSeconds, 0);

      return {
        id: playlist.id,
        name: playlist.name || i18n.t('home:playlistCard.untitled'),
        updatedAt: playlist.updatedAt,
        itemCount,
        itemsLabel: itemCount
          ? i18n.t('home:playlistCard.items', { count: itemCount })
          : i18n.t('home:playlistCard.empty'),
        durationLabel: itemCount
          ? i18n.t('home:playlistCard.loop', {
              duration: formatPlaylistLoopDuration(totalSeconds),
            })
          : null,
        previews: items
          .map((item) => mediaById.get(item.mediaAssetId))
          .filter((asset): asset is MediaAsset => Boolean(asset))
          .slice(0, HOME_CARD_PREVIEW_LIMIT),
        screenCount,
        screensLabel:
          screenCount === 0
            ? i18n.t('home:playlistCard.noScreens')
            : i18n.t('home:playlistCard.screens', { count: screenCount }),
        playing,
        statusLabel: playing
          ? i18n.t('home:playlistCard.playingNow')
          : screenCount > 0
            ? i18n.t('home:playlistCard.assigned')
            : i18n.t('home:playlistCard.notAssigned'),
      };
    });
}

export function sortHomePlaylistCards(
  cards: HomePlaylistCard[],
  sort: HomePlaylistSort,
): HomePlaylistCard[] {
  const next = cards.slice();
  if (sort === 'name') {
    next.sort((a, b) => a.name.localeCompare(b.name));
  } else if (sort === 'screens') {
    next.sort((a, b) => b.screenCount - a.screenCount || a.name.localeCompare(b.name));
  } else {
    next.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }
  return next;
}
